import { useState, useEffect, useCallback, useMemo } from "react";

import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../hooks/useChat";
import { extractDraftFromMessages, extractConversationFromContent } from "../../lib/draftExtractor";
import type { ChatMessage } from "../../types/chat";
import { ConversationHistory } from "../chat/ConversationHistory";
import { MessageInput } from "../chat/MessageInput";
import { DraftPreview } from "./DraftPreview";

/**
 * Besluit (decision) drafting page with split-screen layout.
 * Left side: Conversation for iterative drafting (draft content filtered out)
 * Right side: Live preview of the concept besluit
 */
export function BesluitDrafting() {
  const { isAuthenticated, user, isLoading: authLoading, login } = useAuth();
  const { messages, isLoading, error, rateLimitCountdown, sendMessage, clearConversation } =
    useChat({
      mode: "drafting",
    });

  // Extract current draft from conversation
  const [currentDraft, setCurrentDraft] = useState<string>("");

  // Update draft whenever messages change, but only from assistant messages
  useEffect(() => {
    // Only extract draft if the last message is from the assistant
    // This prevents overwriting user edits while waiting for AI response
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === "assistant") {
      const draft = extractDraftFromMessages(messages);
      if (draft) {
        setCurrentDraft(draft);
      }
    }
  }, [messages]);

  // Filter messages to show only conversation part (no draft markdown)
  const filteredMessages = useMemo((): ChatMessage[] => {
    return messages.map((msg) => {
      if (msg.role === "assistant") {
        // Extract only the conversation part, removing the draft
        const conversationOnly = extractConversationFromContent(msg.content);
        return {
          ...msg,
          content: conversationOnly || msg.content,
        };
      }
      return msg;
    });
  }, [messages]);

  // Handle manual edits to the draft - save locally
  const handleDraftEdit = useCallback((newDraft: string) => {
    setCurrentDraft(newDraft);
  }, []);

  // Send edited draft to AI for review when user saves
  const handleDraftSaveAndReview = useCallback(
    async (editedDraft: string) => {
      setCurrentDraft(editedDraft);
      // Send the edited draft to the AI for feedback
      // Full message goes to AI, short message shown in UI
      const fullMessage = `Ik heb het concept besluit aangepast. Hier is de nieuwe versie:

\`\`\`markdown
${editedDraft}
\`\`\`

Bekijk mijn wijzigingen en geef feedback. Zijn er verbeteringen mogelijk?`;
      const displayMessage = "✏️ Besluit gewijzigd en feedback gevraagd";
      sendMessage(fullMessage, displayMessage);
    },
    [sendMessage],
  );

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <div className="text-center">
          <div className="text-6xl mb-4">🐺</div>
          <p className="text-on-surface-variant">Laden...</p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🐺</div>
          <h1 className="text-2xl font-bold text-on-surface mb-4">Welkom bij Akela</h1>
          <p className="text-on-surface-variant mb-6">
            Log in met je Google Workspace account om een besluit op te stellen.
          </p>
          <button
            onClick={login}
            className="inline-block px-6 py-3 bg-primary text-on-primary rounded-md hover:brightness-110 transition shadow-1"
          >
            Inloggen met Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="bg-surface-container-lowest border-b border-outline-variant shadow-1 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-4xl hover:opacity-80 transition">
              🐺
            </a>
            <div>
              <h1 className="text-xl font-bold text-on-surface">Besluit opstellen</h1>
              <p className="text-sm text-on-surface-variant">
                Conversationeel een besluit schrijven
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="text-sm text-on-surface-variant hover:text-on-surface"
              >
                Nieuw besluit
              </button>
            )}

            {user && (
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                    referrerPolicy="no-referrer"
                  />
                )}
                <div className="text-sm">
                  <div className="font-medium text-on-surface">{user.name}</div>
                  {user.isAdmin && <div className="text-xs text-primary">Admin</div>}
                </div>
              </div>
            )}

            <a
              href="/"
              className="text-sm px-4 py-2 bg-surface-container text-on-surface rounded-md hover:bg-surface-container-high transition"
            >
              ← Terug naar chat
            </a>

            {user?.isAdmin && (
              <a
                href="/admin"
                className="text-sm px-4 py-2 bg-surface-container text-on-surface rounded-md hover:bg-surface-container-high transition"
              >
                Admin
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Split screen content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left side: Conversation (draft content filtered out) */}
        <div className="flex flex-col w-1/2 border-r">
          <ConversationHistory
            messages={filteredMessages}
            userAvatar={user?.picture}
            isLoading={isLoading}
            error={error}
            rateLimitCountdown={rateLimitCountdown}
            collapsibleSources
          />
          <MessageInput
            onSend={sendMessage}
            disabled={isLoading || rateLimitCountdown !== null}
            placeholder="Beschrijf het besluit dat je wilt opstellen..."
          />
        </div>

        {/* Right side: Draft preview */}
        <div className="w-1/2 bg-surface-dim overflow-hidden flex flex-col">
          <DraftPreview
            markdown={currentDraft}
            onEdit={handleDraftEdit}
            onSaveAndReview={(draft) => void handleDraftSaveAndReview(draft)}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
